import * as React from 'react'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'
import { GpsAndBorderCrossingConfigurationModel } from '@/lib/api/company/configuration/gps/types'
import { FieldsetInput } from '../../components/FieldsetInput'
import { FieldsetSwitch } from '../../components/FieldsetSwitch'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  isSaving: boolean
  state: GpsAndBorderCrossingConfigurationModel
  setState: React.Dispatch<
    React.SetStateAction<GpsAndBorderCrossingConfigurationModel>
  >
}

export const GpsForm = ({ isSaving, state, setState }: Props) => {
  const { t } = useLocalize()
  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target

      switch (type) {
        case 'number':
          setState((state) => ({
            ...state,
            [name]: type === 'number' ? Number(value) : value,
          }))
          break
        case 'checkbox':
          setState((state) => ({
            ...state,
            [name]: checked,
          }))
          break
        default:
          setState((state) => ({
            ...state,
            [name]: value,
          }))
          break
      }
    },
    [],
  )

  return (
    <SettingsSectionWithTabs>
      <div
        data-tab-name="a"
        title={t('configuration-gps.tab-gps-borders')}
        className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
      >
        {/*
         * name: countryAliasesWithMinWage
         * st_api: minimumWageCountry
         * Title: Aliases of countries with minimum wage (comma-separated list)
         * PL: Aliasy krajów objętych płacą minimalną (lista oddzielona przecinkiem)
         */}
        <FieldsetInput
          label={t('configuration-gps.country-aliases-min-wage')}
          value={state.countryAliasesWithMinWage}
          name="countryAliasesWithMinWage"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
          type="text"
        />

        {/**
         * name:  restActivityAliases
         * st_api: restActivity
         * Title: Aliases of rest activities (comma-separated list)
         * PL: Aliasy aktywności odpoczynku (lista oddzielona przecinkiem)
         */}
        <FieldsetInput
          label={t('configuration-gps.rest-activity-aliases')}
          value={state.restActivityAliases}
          name="restActivityAliases"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
          type="text"
        />

        {/**
         * name: drivingActivityAliases
         * st_api: drivingActivity
         * Title: Aliases of driving activities (comma-separated list)
         * PL: Aliasy aktywności jazdy (lista oddzielona przecinkiem)
         */}
        <FieldsetInput
          label={t('configuration-gps.driving-activity-aliases')}
          value={state.drivingActivityAliases}
          name="drivingActivityAliases"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
          type="text"
        />

        {/**
         * name: tmsUnloadTime
         * Title: TMS data unloading time
         * PL: Godzina rozładunku danych TMS
         */}
        <FieldsetInput
          label={t('configuration-gps.tms-unload-time')}
          value={state.tmsUnloadTime}
          name="tmsUnloadTime"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
          type="time"
        />

        {/**
         * name: showAutomaticBorderCrossings
         * Title: Show automatic border crossings by default
         * PL: Pokaż domyślnie automatyczne przekroczenia granic
         */}
        <FieldsetSwitch
          label={t('configuration-gps.show-auto-border-crossings')}
          checked={state.showAutomaticBorderCrossings}
          name="showAutomaticBorderCrossings"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        {/**
         * name: hideSameCountryCrossings
         * Title: Hide border crossings within the same country if too far apart
         * PL: Nie pokazuj przekroczeń granic tego samego kraju oddalonych od siebie
         */}
        <FieldsetSwitch
          label={t('configuration-gps.hide-same-country-crossings')}
          checked={state.hideSameCountryCrossings}
          name="hideSameCountryCrossings"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        >
          {/**
           * name: hideSameCountryCrossingThresholdMin
           * Title: Time threshold (in minutes) for hiding same-country border crossings
           * PL: Próg czasowy (w minutach) dla ukrywania przekroczeń granic tego samego kraju
           */}
          <FieldsetInput
            label={t('configuration-gps.same-country-threshold')}
            value={state.hideSameCountryCrossingThresholdMin?.toString()}
            name="hideSameCountryCrossingThresholdMin"
            placeholder=""
            onChange={handleInputChange}
            disabled={isSaving || state.hideSameCountryCrossings !== true}
            type="number"
            wrapperClassName='tw-mt-2'
          />
        </FieldsetSwitch>

        {/**
         * name: showGpsCrossingsTogether
         * Title: Show GPS border crossings in one table with automatic ones
         * PL: Pokaż przekroczenia granic z GPS w jednej tabelce z granicami automatycznymi
         */}
        <FieldsetSwitch
          label={t('configuration-gps.show-gps-crossings-together')}
          checked={state.showGpsCrossingsTogether}
          name="showGpsCrossingsTogether"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />
      </div>
    </SettingsSectionWithTabs>
  )
}
